import { useState, useMemo, useEffect } from "react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faPen,
  faMoneyBillWave,
  faCalendarPlus,
  faSpinner,
  faToggleOn,
  faToggleOff,
} from "@fortawesome/free-solid-svg-icons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import SearchBar from "../../../components/ui/SearchBar";
import TableWrapper from "../../../components/ui/TableWrapper";
import EmptyState from "../../../components/ui/EmptyState";
import Pagination from "../../../components/ui/Pagination";
import DateFilter from "../../../components/ui/DateFilter";
import AddCapitalModal from "./AddCapitalModal";
import EditCapitalModal from "./EditCapitalModal";
import { useShopCapitals } from "../../../hooks/useCapital";
import { useAuth } from "../../../hooks";
import { toastSuccess, toastError } from "../../../utils/toast";

const formatCurrency = (n) => `TZS ${Number(n).toLocaleString()}`;

const formatDate = (date) => {
  if (!date) return "-";
  const d = new Date(date);
  return d.toISOString().split("T")[0];
};

const CapitalTab = ({ shop }) => {
  const { hasPermission } = useAuth();
  const canManageCapital = hasPermission([
    "manage_capital",
    "manage_shop_modules",
  ]);

  const {
    capitals,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    createCapital,
    updateCapital,
    toggleCapitalStatus,
    refresh,
  } = useShopCapitals(shop.shopId);

  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [editingCapital, setEditingCapital] = useState(null);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const filteredCapitals = useMemo(() => {
    let result = capitals || [];

    if (isFilterApplied && startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      result = result.filter((cap) => {
        const capitalDate = new Date(cap.date || cap.created_at);
        return capitalDate >= start && capitalDate <= end;
      });
    }

    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase().trim();
      result = result.filter(
        (cap) =>
          cap.amount?.toString().includes(search) ||
          cap.date?.includes(search) ||
          cap.shop?.shop_name?.toLowerCase().includes(search),
      );
    }

    if (statusFilter === "active") {
      result = result.filter(
        (cap) => cap.status === "active" || cap.status === "ACTIVE",
      );
    } else if (statusFilter === "inactive") {
      result = result.filter(
        (cap) => cap.status === "inactive" || cap.status === "INACTIVE",
      );
    }

    return result.sort(
      (a, b) =>
        new Date(b.date || b.created_at) - new Date(a.date || a.created_at),
    );
  }, [capitals, searchTerm, statusFilter, startDate, endDate, isFilterApplied]);

  const totalItems = filteredCapitals.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const currentCapitals = filteredCapitals.slice(
    startIndex,
    startIndex + pageSize,
  );

  const totalCapital =
    capitals?.reduce((sum, cap) => sum + parseFloat(cap.amount || 0), 0) || 0;

  const handleAddCapital = async (data) => {
    try {
      await createCapital({
        ...data,
        shopId: shop.shopId,
      });
      toastSuccess("Capital added successfully!");
      setIsAdding(false);
      refresh();
    } catch (error) {
      toastError(error?.response?.data?.message || "Failed to add capital");
    }
  };

  const handleEditCapital = async (data) => {
    try {
      await updateCapital(data.capitalId, data);
      toastSuccess("Capital updated successfully!");
      setEditingCapital(null);
      refresh();
    } catch (error) {
      toastError(error?.response?.data?.message || "Failed to update capital");
    }
  };

  const handleToggleStatus = async (capitalId, currentStatus) => {
    try {
      await toggleCapitalStatus(capitalId);
      toastSuccess(
        `Capital ${currentStatus === "active" || currentStatus === "ACTIVE" ? "deactivated" : "activated"} successfully!`,
      );
      refresh();
    } catch (error) {
      toastError(error?.response?.data?.message || "Failed to update status");
    }
  };

  if (loading && capitals.length === 0) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <FontAwesomeIcon
            icon={faSpinner}
            className="h-8 w-8 text-blue-600 animate-spin"
          />
          <p className="text-gray-600 text-sm">Loading capital records...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <p className="text-red-600">
          Failed to load capital records. Please try again.
        </p>
        <Button
          onClick={refresh}
          className="ml-4 bg-blue-800 hover:bg-blue-900 text-white"
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <AddCapitalModal
        isOpen={isAdding}
        onClose={() => setIsAdding(false)}
        onSave={handleAddCapital}
        shopId={shop.shopId}
      />

      <EditCapitalModal
        capitalEntry={editingCapital}
        isOpen={!!editingCapital}
        onClose={() => setEditingCapital(null)}
        onUpdate={handleEditCapital}
      />

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-700">
            Capital History
          </h3>
          <p className="text-sm text-gray-600">
            Manage capital contributions for this shop
          </p>
        </div>
        {canManageCapital && (
          <Button
            size="sm"
            onClick={() => setIsAdding(true)}
            className="gap-2 bg-blue-800 hover:bg-blue-900 text-white shadow-sm"
          >
            <FontAwesomeIcon icon={faPlus} className="h-3 w-3" />
            Add Capital
          </Button>
        )}
      </div>

      <DateFilter
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onApply={() => {
          setIsFilterApplied(true);
          setCurrentPage(1);
        }}
        onClear={() => {
          setStartDate("");
          setEndDate("");
          setIsFilterApplied(false);
          setCurrentPage(1);
        }}
        isFilterApplied={isFilterApplied}
        totalItems={totalItems}
        label="capital records"
      />

      <div className="flex flex-col sm:flex-row gap-3">
        <SearchBar
          value={searchTerm}
          onChange={(value) => {
            setSearchTerm(value);
            setCurrentPage(1);
          }}
          placeholder="Search by amount or date..."
          className="flex-1"
        />

        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => {
              setStatusFilter("all");
              setCurrentPage(1);
            }}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
              statusFilter === "all"
                ? "bg-blue-800 text-white shadow-sm"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-200/50"
            }`}
          >
            All
          </button>
          <button
            onClick={() => {
              setStatusFilter("active");
              setCurrentPage(1);
            }}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
              statusFilter === "active"
                ? "bg-blue-800 text-white shadow-sm"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-200/50"
            }`}
          >
            Active
          </button>
          <button
            onClick={() => {
              setStatusFilter("inactive");
              setCurrentPage(1);
            }}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
              statusFilter === "inactive"
                ? "bg-blue-800 text-white shadow-sm"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-200/50"
            }`}
          >
            Inactive
          </button>
        </div>
      </div>

      <TableWrapper
        title="capital records"
        totalItems={totalItems}
        currentPage={currentPage}
        totalPages={totalPages}
        className="bg-white border border-gray-200 shadow-sm overflow-hidden"
      >
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50 hover:bg-gray-50 border-b-2 border-gray-200">
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                S/N
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                <FontAwesomeIcon
                  icon={faMoneyBillWave}
                  className="mr-1.5 h-3 w-3"
                />
                Capital Amount
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                <FontAwesomeIcon
                  icon={faCalendarPlus}
                  className="mr-1.5 h-3 w-3"
                />
                Added Date
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                <FontAwesomeIcon
                  icon={faCalendarPlus}
                  className="mr-1.5 h-3 w-3"
                />
                Created At
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                <FontAwesomeIcon
                  icon={faCalendarPlus}
                  className="mr-1.5 h-3 w-3"
                />
                Updated At
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                Status
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4 text-center">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentCapitals.length > 0 ? (
              currentCapitals.map((cap, idx) => {
                const globalIndex = startIndex + idx + 1;
                const status = cap.status?.toUpperCase() || "ACTIVE";
                return (
                  <TableRow
                    key={cap.capitalId || cap.id}
                    className="hover:bg-blue-50/30 transition-colors duration-150 border-b border-gray-100"
                  >
                    <TableCell className="py-3 px-4 text-sm text-gray-600">
                      {globalIndex}
                    </TableCell>
                    <TableCell className="py-3 px-4 font-semibold text-gray-900 text-sm">
                      {formatCurrency(cap.amount)}
                    </TableCell>
                    <TableCell className="py-3 px-4 text-sm text-gray-600">
                      <span className="flex items-center gap-1.5">
                        <FontAwesomeIcon
                          icon={faCalendarPlus}
                          className="text-blue-500 h-3 w-3"
                        />
                        {formatDate(cap.date)}
                      </span>
                    </TableCell>
                    <TableCell className="py-3 px-4 text-sm text-gray-600">
                      <span className="flex items-center gap-1.5">
                        <FontAwesomeIcon
                          icon={faCalendarPlus}
                          className="text-blue-500 h-3 w-3"
                        />
                        {formatDate(cap.created_at)}
                      </span>
                    </TableCell>
                    <TableCell className="py-3 px-4 text-sm text-gray-600">
                      <span className="flex items-center gap-1.5">
                        <FontAwesomeIcon
                          icon={faCalendarPlus}
                          className="text-blue-500 h-3 w-3"
                        />
                        {formatDate(cap.updated_at)}
                      </span>
                    </TableCell>
                    <TableCell className="py-3 px-4">
                      <Badge
                        variant="outline"
                        className={`${
                          status === "ACTIVE"
                            ? "bg-green-50 text-green-700 border-green-200"
                            : "bg-red-50 text-red-700 border-red-200"
                        } border font-normal px-3 py-1 rounded-full text-xs flex items-center gap-1.5`}
                      >
                        <span
                          className={`inline-block w-1.5 h-1.5 rounded-full ${
                            status === "ACTIVE" ? "bg-green-500" : "bg-red-500"
                          }`}
                        />
                        {status}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-3 px-4">
                      <div className="flex gap-2 justify-center">
                        {canManageCapital ? (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setEditingCapital(cap)}
                              className="gap-1 border-gray-300 hover:border-blue-800 hover:bg-blue-50 text-gray-700 transition-all duration-200"
                            >
                              <FontAwesomeIcon
                                icon={faPen}
                                className="h-3 w-3"
                              />{" "}
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                handleToggleStatus(
                                  cap.capitalId || cap.id,
                                  cap.status,
                                )
                              }
                              className={`gap-1 ${
                                status === "ACTIVE"
                                  ? "border-yellow-500 text-yellow-600 hover:bg-yellow-50"
                                  : "border-green-500 text-green-600 hover:bg-green-50"
                              }`}
                            >
                              <FontAwesomeIcon
                                icon={
                                  status === "ACTIVE" ? faToggleOff : faToggleOn
                                }
                                className="h-3 w-3"
                              />
                              {status === "ACTIVE" ? "Deactivate" : "Activate"}
                            </Button>
                          </>
                        ) : (
                          <span className="text-xs text-gray-500">—</span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">
                  <EmptyState
                    title="No capital records found"
                    message={
                      searchTerm || isFilterApplied
                        ? "Try adjusting your search or filters"
                        : "Add your first capital contribution to get started"
                    }
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          {totalCapital > 0 && (
            <tfoot>
              <TableRow className="bg-gradient-to-r from-blue-50 to-blue-100/50 font-bold">
                <TableCell className="py-3 px-4 text-sm font-semibold text-gray-700"></TableCell>
                <TableCell className="py-3 px-4 font-bold text-blue-700 text-sm"></TableCell>
                <TableCell className="py-3 px-4" colSpan={1}></TableCell>
                <TableCell className="py-3 px-4" colSpan={1}></TableCell>
                <TableCell className="py-3 px-4" colSpan={1}></TableCell>
                <TableCell className="py-3 px-4 text-center">
                  <span className="text-sm font-semibold text-black px-3 py-1 rounded-full">
                    Total :
                  </span>
                </TableCell>
                <TableCell className="py-3 px-4 font-bold text-blue-700 text-sm">
                  {formatCurrency(totalCapital)}
                </TableCell>
              </TableRow>
            </tfoot>
          )}
        </Table>
      </TableWrapper>

      {totalItems > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        />
      )}
    </div>
  );
};

export default CapitalTab;
